import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Table, Select, Input, Button, Spin } from 'antd';
import Header from './header';
import Sidebar from './sidebar';
import '../style/TableStyle.css';

const { Option } = Select;

const HouseList = () => {
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [size, setSize] = useState(20);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchField, setSearchField] = useState('id');
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [munToleId, setMunToleId] = useState(null);
  const [toleOptions, setToleOptions] = useState([]);

  const API_URL = 'https://ward3-chandrapur-api.palikaapp.com/api/house';
  const SEARCH_API_URL = 'https://ward3-chandrapur-api.palikaapp.com/api/house/search';
  const FILTER_URL = 'https://ward3-chandrapur-api.palikaapp.com/api/house/filter-tole';

  useEffect(() => {
    const fetchHouses = async () => {
      try {
        setLoading(true);
        let response;
        if (munToleId) {
          response = await axios.get(FILTER_URL, {
            params: { munTole: munToleId, page, size },
          });
        } else {
          response = await axios.get(`${API_URL}/pagination`, {
            params: { page, size, sortField, sortOrder },
          });
        }
        setHouses(response.data.content);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchHouses();
  }, [page, size, sortField, sortOrder, munToleId]);

  useEffect(() => {
    const fetchToles = async () => {
      try {
        const response = await axios.get(
          'https://ward3-chandrapur-api.palikaapp.com/api/mun-tole/all'
        );
        setToleOptions(response.data);
      } catch (error) {
        console.error('Error fetching tole options', error);
      }
    };
    fetchToles();
  }, []);

  const searchHouses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(SEARCH_API_URL, {
        params: { keyword: searchKeyword, searchByFieldName: searchField },
      });
      setHouses(response.data);
      setTotalPages(1);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'क्र.सं.', dataIndex: 'id', key: 'id' },
    { title: 'घरधनीको नाम', dataIndex: 'ownerNameNep', key: 'ownerNameNep' },
    { title: 'घरको वर्ग', dataIndex: 'houseType', key: 'houseType' },
    { title: 'घरको प्रकार', dataIndex: 'houseCategory', key: 'houseCategory' },
    { title: 'टोल', dataIndex: 'munToleName', key: 'munToleName' },
    {
      title: 'विवरण',
      key: 'details',
      render: (text, record) => (
        <Link to={`/house/${record.id}/details`} className="text-blue-500">
          बिस्तृत विवरण
        </Link>
      ),
    },
  ];
  const rowClassName = (record, index) => {
    return index % 2 === 0
      ? 'even-row'
      : 'odd-row';
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="flex pt-20">
        <Sidebar />
        <div className="flex-1 p-4 md:p-6 mt-10 lg:ml-64">
          <h1 className="text-2xl font-bold mb-6 text-center md:text-left">
            घरको सुची
          </h1>

          {/* Search and Filter Section */}
          <div className="flex flex-col md:flex-row md:justify-between items-center mb-6 space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-4">
              {/* Search */}
              <Select
                value={searchField}
                onChange={(value) => setSearchField(value)}
                className="w-40"
              >
                <Option value="ownerNameEng">घरधनीको नाम</Option>
                <Option value="buildYear">बनाको वर्ष</Option>
                <Option value="citizenshipNo">नागरिकता नं</Option>
              </Select>
              <Input
                placeholder="खोज..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="w-52"
              />
              <Button type="primary" onClick={searchHouses} className="bg-blue-500">
                खोज
              </Button>
            </div>

            {/* Sorting */}
            <div className="flex items-center space-x-4">
              <label>Sort By:</label>
              <Select
                value={sortField}
                onChange={(value) => setSortField(value)}
                className="w-40"
              >
                <Option value="id">क्र.सं.</Option>
                <Option value="ownerNameEng">घरधनी</Option>
              </Select>
              <Select
                value={sortOrder}
                onChange={(value) => setSortOrder(value)}
                className="w-40"
              >
                <Option value="asc">माथिबाट</Option>
                <Option value="desc">तलबाट</Option>
              </Select>
            </div>
          </div>

          {/* Tole Filter */}
          <div className="flex flex-wrap items-center space-x-2 mb-4">
            <label>टोल:</label>
            <Select
              value={munToleId}
              onChange={(value) => setMunToleId(value)}
              placeholder="सबै टोल"
              className="w-40"
            >
              {toleOptions.map((tole) => (
                <Option key={tole.id} value={tole.id}>
                  {tole.name}
                </Option>
              ))}
            </Select>
          </div>

          {/* Table Section */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spin size="large" />
            </div>
          ) : (
            <div className="overflow-x-auto bg-white rounded-lg shadow">
              <Table
                dataSource={houses}
                columns={columns}
                rowClassName={rowClassName}
                rowKey="id"
                pagination={{
                  current: page + 1,
                  pageSize: size,
                  total: totalPages * size,
                  onChange: (newPage, newPageSize) => {
                    setPage(newPage - 1);
                    setSize(newPageSize);
                  },
                }}
                bordered
                className="custom-table"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HouseList;
